import { c as _c } from "react/compiler-runtime";
import { styled } from '@precis-digital/kurama/utils';
import Tooltip, { type TooltipProps } from '@precis-digital/kurama/Tooltip';
import Typography from '@precis-digital/kurama/Typography';
import { type ReactElement, type Ref } from 'react';
import AlertSymbol from './AlertSymbol';
import type { AlertTFunction, AlertType } from './types';
export const generateTooltipPropsFromAlertText = ({
  alert,
  alertText,
  description,
  descriptionBody
}: TooltipPropsWithAlert, t: AlertTFunction): TooltipProps | undefined => {
  const withAlert = alert != null || alertText != null;
  if (withAlert) {
    const actualDescriptionBody: ReactElement | undefined = descriptionBody ?? (description == null ? undefined : <Typography variant="body2">{description}</Typography>);
    const body = alertText == null ? actualDescriptionBody : <StyledTooltipLines>
          {actualDescriptionBody}
          <Typography variant="body3">{alertText}</Typography>
        </StyledTooltipLines>;
    const title = <StyledTooltipLine>
        <Typography variant="body2" key="alert-text">
          {t(alert ?? 'warning')}
        </Typography>
        <AlertSymbol alert={alert} width="1em" height="1em" key="alert-symbol" />
      </StyledTooltipLine>;
    return body == null ? {
      kind: 'singleline',
      title
    } : {
      kind: 'multiline',
      title,
      body
    };
  }
  if (descriptionBody != null) {
    return {
      kind: 'multiline',
      title: description,
      body: descriptionBody
    };
  }
  if (description != null) {
    return {
      kind: 'singleline',
      title: description
    };
  }
  return undefined;
};
export type TooltipPropsWithAlert = {
  alert?: AlertType | null;
  alertText?: string | null;
} & ({
  description?: string;
  descriptionBody?: never;
} | {
  description: string;
  descriptionBody: ReactElement;
});
export const generateTooltipFromDescriptionAndOrAlert = ({
  element,
  spanInTooltip,
  key,
  t,
  ...tooltipPropwWithAlert
}: {
  element: ReactElement;
  spanInTooltip?: boolean;
  key?: string;
  t: AlertTFunction;
} & TooltipPropsWithAlert): ReactElement => {
  const tooltipProps = generateTooltipPropsFromAlertText(tooltipPropwWithAlert, t);
  if (tooltipProps == null) {
    return element;
  }
  return <Tooltip key={key} {...tooltipProps}>
      {spanInTooltip === true ? <span>{element}</span> : element}
    </Tooltip>;
};
type AlertTooltipProps = {
  element?: ReactElement | null;
  key?: string;
  ref?: Ref<HTMLDivElement>;
  t?: AlertTFunction;
} & TooltipPropsWithAlert;
const defaultTranslations: Record<AlertType, string> = {
  info: 'Info',
  warning: 'Warning',
  error: 'Error'
};
const defaultTFunction: AlertTFunction = (key, props) => {
  if (props != null) {
    return defaultTranslations[key].replaceAll(/{{(.*?)}}/g, (match, p1) => props[p1] ?? match);
  }
  return defaultTranslations[key];
};
export const AlertTooltip = t0 => {
  const $ = _c(12);
  let element;
  let ref;
  let t1;
  let tooltipPropwWithAlert;
  if ($[0] !== t0) {
    const {
      element: t2,
      ref: t3,
      t,
      ...t4
    } = t0;
    element = t2;
    ref = t3;
    tooltipPropwWithAlert = t4;
    t1 = generateTooltipPropsFromAlertText(tooltipPropwWithAlert, t ?? defaultTFunction);
    $[0] = t0;
    $[1] = element;
    $[2] = ref;
    $[3] = t1;
    $[4] = tooltipPropwWithAlert;
  } else {
    element = $[1];
    ref = $[2];
    t1 = $[3];
    tooltipPropwWithAlert = $[4];
  }
  const tooltipProps = t1;
  if (tooltipProps == null) {
    return element ?? null;
  }
  let t2;
  if ($[5] !== element || $[6] !== ref || $[7] !== tooltipPropwWithAlert) {
    t2 = element ?? <span ref={ref} style={{
      display: "flex",
      height: "24px",
      width: "24px",
      justifyContent: "center",
      alignItems: "center"
    }}><AlertSymbol alert={tooltipPropwWithAlert.alert} /></span>;
    $[5] = element;
    $[6] = ref;
    $[7] = tooltipPropwWithAlert;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== t2 || $[10] !== tooltipProps) {
    t3 = <Tooltip {...tooltipProps}>{t2}</Tooltip>;
    $[9] = t2;
    $[10] = tooltipProps;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
};
AlertTooltip.displayName = 'AlertTooltip';
const StyledTooltipLine = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  width: '100%'
}));
const StyledTooltipLines = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px'
}));
export default AlertTooltip;